import { ReactNode, useState, useRef } from "react";
import {
  RegisterOptions,
  UseFormRegister,
  FieldError,
  FieldValues,
  Path,
} from "react-hook-form";
import { ReactComponent as EyeIconSvg } from "../../assets/ic_eye.svg";
import { ReactComponent as EyeOffIconSvg } from "../../assets/ic_eye_off.svg";

interface CustomInputProps<T extends FieldValues> {
  icon?: ReactNode;
  placeholder?: string;
  type?: string;
  error?: FieldError;
  register: UseFormRegister<T>;
  name: Path<T>;
  validation?: RegisterOptions<T, Path<T>>;
  disabled?: boolean;
  label?: string;
  min?: number;
  max?: number;
  step?: number;
  autoFocus?: boolean;
  defaultCalendarDate?: string; // For date inputs, sets the calendar position when opened
}

const CustomInput = <T extends FieldValues>({
  icon,
  placeholder,
  type = "text",
  error,
  register,
  name,
  validation,
  disabled,
  label,
  min,
  max,
  step,
  autoFocus,
  defaultCalendarDate,
}: CustomInputProps<T>) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const isPasswordField = type === "password";
  const inputType = isPasswordField
    ? showPassword
      ? "text"
      : "password"
    : type;

  const registerProps = register(name, validation);

  // Merge the refs
  const mergedRef = (element: HTMLInputElement) => {
    inputRef.current = element;
    registerProps.ref(element);
  };

  const EyeIcon = ({ show }: { show: boolean }) =>
    show ? (
      <EyeIconSvg width={20} height={20} />
    ) : (
      <EyeOffIconSvg width={20} height={20} />
    );

  const handleContainerClick = () => {
    if (type === "date" && inputRef.current && !disabled) {
      // If the input is empty and we have a default calendar date, set it to position the calendar
      if (!inputRef.current.value && defaultCalendarDate) {
        // Temporarily set the value to position the calendar
        inputRef.current.value = defaultCalendarDate;

        // Trigger change event so react-hook-form is aware
        const event = new Event("change", { bubbles: true });
        inputRef.current.dispatchEvent(event);
      }

      inputRef.current.focus();
      // For date inputs, we might also need to show the picker
      if (inputRef.current.showPicker) {
        inputRef.current.showPicker();
      }
    }
  };

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-slate-300 mb-2">
          {label}
        </label>
      )}
      <div
        className={`
        relative flex items-center
        bg-gray-800/50 backdrop-blur-sm
        border rounded-lg
        transition-all duration-200 ease-in-out
        ${error ? "border-red-500" : isFocused ? "border-blue-500" : "border-gray-600"}
        ${isFocused ? "shadow-lg shadow-blue-500/20" : ""}
      `}
      >
        {icon && (
          <div
            className={`flex items-center justify-center w-12 h-12 text-gray-400 ${
              type === "date"
                ? "cursor-pointer hover:text-gray-300 transition-colors"
                : ""
            }`}
            onClick={type === "date" ? handleContainerClick : undefined}
            title={type === "date" ? "Click to open calendar" : undefined}
          >
            {icon}
          </div>
        )}

        <input
          ref={mergedRef}
          type={inputType}
          placeholder={placeholder}
          autoFocus={autoFocus}
          className={`
            flex-1 bg-transparent text-white placeholder-gray-400
            px-3 py-3
            focus:outline-none
            disabled:opacity-50 disabled:cursor-not-allowed
            ${isPasswordField ? "rounded-none" : "rounded-r-lg"}
            ${type === "date" ? "cursor-text" : ""}
          `}
          onFocus={() => setIsFocused(true)}
          disabled={disabled}
          min={min}
          max={max}
          step={step}
          name={registerProps.name}
          onChange={registerProps.onChange}
          onBlur={(e) => {
            setIsFocused(false);
            registerProps.onBlur(e);
          }}
          onKeyDown={(e) => {
            // For date inputs, allow manual typing by preventing some default behaviors
            if (type === "date") {
              // Allow all alphanumeric, backspace, delete, tab, escape, enter, and arrow keys
              const allowedKeys = [
                "Backspace",
                "Delete",
                "Tab",
                "Escape",
                "Enter",
                "ArrowLeft",
                "ArrowRight",
                "ArrowUp",
                "ArrowDown",
                "Home",
                "End",
              ];

              // Allow numbers and common date separators
              const isNumber = e.key >= "0" && e.key <= "9";
              const isDateSeparator =
                e.key === "/" || e.key === "-" || e.key === ".";

              if (
                !allowedKeys.includes(e.key) &&
                !isNumber &&
                !isDateSeparator &&
                !e.ctrlKey &&
                !e.metaKey
              ) {
                // Don't prevent default for date inputs to allow native behavior
                return;
              }
            }
          }}
        />

        {isPasswordField && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="flex items-center justify-center w-12 h-12 text-gray-400 hover:text-gray-200 transition-colors duration-200 rounded-r-lg"
            disabled={disabled}
          >
            <EyeIcon show={showPassword} />
          </button>
        )}
      </div>

      {error && (
        <p className="mt-2 text-sm text-red-400 animate-pulse">
          {error.message}
        </p>
      )}
    </div>
  );
};

export default CustomInput;
